import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, withQuery, money, quantity } from '../api.js';
import { useAuth } from '../App.jsx';
import GroupPicker from '../components/GroupPicker.jsx';

const PAGE = 100;

export default function Products() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({ q: '', groupId: '', warehouseId: '', inStock: false });
  const [data, setData] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/api/product-groups'), api.get('/api/warehouses')])
      .then(([g, w]) => { setGroups(g); setWarehouses(w); })
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    // Изчакваме малко след последната буква, за да не питаме на всеки знак.
    const timer = setTimeout(() => {
      setLoading(true);
      api.get(withQuery('/api/products', { ...filters, limit: PAGE, offset }))
        .then(setData)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, filters.q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [filters, offset]);

  function change(patch) {
    setOffset(0);
    setFilters((f) => ({ ...f, ...patch }));
  }

  return (
    <>
      <div className="page-head">
        <h1>Стоки и наличности</h1>
        <div className="spacer" />
        {user.role !== 'auditor' && (
          <Link to="/products/new"><button className="primary">+ Нова стока</button></Link>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <label className="field">
          Търсене
          <input
            type="search"
            autoComplete="off"
            placeholder="име, каталожен номер или бърз код"
            value={filters.q}
            onChange={(e) => change({ q: e.target.value })}
          />
        </label>

        <label className="field">
          Група
          <GroupPicker
            label="всички"
            value={filters.groupId}
            valueLabel={groups.find((g) => String(g.id) === String(filters.groupId))?.name}
            onPick={(id) => change({ groupId: id })}
            onClear={() => change({ groupId: '' })}
          />
        </label>

        <label className="field">
          Склад
          <select value={filters.warehouseId} onChange={(e) => change({ warehouseId: e.target.value })}>
            <option value="">общо</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>

        <label style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 7 }}>
          <input
            type="checkbox"
            checked={filters.inStock}
            onChange={(e) => change({ inStock: e.target.checked })}
          />
          само с наличност
        </label>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Наименование</th>
                <th>Група</th>
                <th>Кат. №</th>
                <th>Бърз код</th>
                <th className="num">Наличност</th>
                <th className="num">Доставна</th>
                <th className="num">Едро</th>
                <th className="num">Дребно</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link to={`/products/${p.id}`}>{p.name}</Link>
                    {!p.is_active && <span className="badge muted" style={{ marginLeft: 6 }}>неактивна</span>}
                  </td>
                  <td className="muted">{p.group_name}</td>
                  <td className="muted">{p.code ?? '—'}</td>
                  <td className="muted">{p.barcode ?? '—'}</td>
                  <td className="num">
                    <span className={Number(p.quantity) > 0 ? '' : 'muted'}>
                      {quantity(p.quantity)} {p.unit}
                    </span>
                  </td>
                  <td className="num">{money(p.purchase_price, p.currency)}</td>
                  <td className="num">{money(p.wholesale_price, p.currency)}</td>
                  <td className="num">{money(p.retail_price, p.currency)}</td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="empty">Няма намерени стоки</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <Pager total={data.total} offset={offset} setOffset={setOffset} loading={loading} />
      </div>
    </>
  );
}

export function Pager({ total, offset, setOffset, loading, page = PAGE }) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + page, total);
  return (
    <div className="pager">
      <button disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - page))}>
        ← назад
      </button>
      <button disabled={to >= total || loading} onClick={() => setOffset(offset + page)}>
        напред →
      </button>
      <span>{loading ? 'зареждане…' : `${from}–${to} от ${total.toLocaleString('bg-BG')}`}</span>
    </div>
  );
}
